import { query } from "@solidjs/router";
import { env } from "~/config/env";
import { buildMetadataHistoryUrl } from "../queryUrls";
import type { MetadataHistoryResponse } from "~/types/metadataHistory";
import type { IcyMetadata } from "~/types/metadata";
import { fetchFromCMS, CMSError } from "../queryUtils";
import { logger } from "~/lib/logger/logger";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";
import { createSingleFlight } from "~/lib/cache/singleFlight";

/**
 * In-memory server-side cache for now-playing data.
 *
 * Now-playing metadata is identical for all users on the same channel,
 * so we cache it to avoid hammering the CMS with per-user requests.
 * With 13 channels and 15s TTL, the CMS sees at most ~52 req/min per worker
 * instead of thousands.
 */
const CACHE_TTL_MS = 15_000;
const MAX_CACHE_SIZE = 100;
const nowPlayingCache = new Map<string, { data: IcyMetadata | null; expiresAt: number }>();
const loadOnce = createSingleFlight<string, IcyMetadata | null>();

/**
 * Server-side query for fetching the current now-playing track.
 *
 * Uses fetchFromCMS for timeout (8s) and circuit breaker protection.
 * On CMS failure: returns stale cached data (or null) instead of throwing,
 * because a metadata outage should never crash the audio player.
 */
export const getNowPlayingQuery = query(
  async (channelIdentifier: string): Promise<IcyMetadata | null> => {
    "use server";

    const cached = nowPlayingCache.get(channelIdentifier);
    if (cached && Date.now() < cached.expiresAt) {
      touchEntry(nowPlayingCache, channelIdentifier);
      return cached.data;
    }

    return loadOnce(channelIdentifier, async () => {
      try {
        const url = `${env.cmsApiUrl}${buildMetadataHistoryUrl({ channelIdentifier, limit: 1 })}`;
        const data = await fetchFromCMS<MetadataHistoryResponse>(url, {
          logPrefix: "[nowPlaying]",
          category: "metadata",
        });

        const entry = data?.entries?.[0];
        const result = entry?.title ? { title: entry.title, artist: entry.artist } : null;

        evictIfFull(nowPlayingCache, MAX_CACHE_SIZE);
        nowPlayingCache.set(channelIdentifier, {
          data: result,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });

        return result;
      } catch (error) {
        if (error instanceof CMSError) {
          logger.warn(
            `[nowPlaying] CMS ${error.reason} — ${cached ? "returning stale cache" : "no cache available, returning null"} for ${channelIdentifier}`
          );
        } else {
          logger.error(
            `[nowPlaying] Unexpected error for ${channelIdentifier}:`,
            error instanceof Error ? error.message : error
          );
        }
        // Return stale cache (even if expired) so the player shows last-known track
        return cached?.data ?? null;
      }
    });
  },
  "nowPlaying"
);
